import { GiCeremonialMask, GiBookCover, GiKnifeFork } from "react-icons/gi";
import { FaTshirt, FaMusic } from "react-icons/fa";
import { BsPersonArmsUp } from "react-icons/bs";
import { useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";
import LeftSidebar from "../components/LeftSidebar.tsx";
import RightSidebar from "../components/RightSidebar.tsx";
import BottomNav from "../components/BottomNav.tsx";
import TopProfileBar from "../components/TopProfileBar.tsx";
import GenericCard from "../components/sastra-budaya/GenericCard.tsx";
import SongCard from "../components/sastra-budaya/SongCard.tsx";
import { clothing } from "../data/sastra-budaya/clothing.ts";
import { customs } from "../data/sastra-budaya/customs.ts";
import { dances } from "../data/sastra-budaya/dances.ts";
import { foods } from "../data/sastra-budaya/foods.ts";
import { songs } from "../data/sastra-budaya/songs.ts";
import { stories } from "../data/sastra-budaya/stories.ts";
import { getUserData } from "../utils/userStorage.ts";
import DotsBackground from "../components/DotsBackground.tsx";
import { Instagram, Linkedin } from "lucide-react";

const tabs = [
  {
    value: "cerita-rakyat",
    label: "Cerita Rakyat",
    icon: <GiBookCover className="w-4 h-4" />,
  },
  {
    value: "lagu-tradisional",
    label: "Lagu",
    icon: <FaMusic className="w-4 h-4" />,
  },
  {
    value: "adat-istiadat",
    label: "Adat",
    icon: <GiCeremonialMask className="w-4 h-4" />,
  },
  {
    value: "pakaian-adat",
    label: "Pakaian",
    icon: <FaTshirt className="w-4 h-4" />,
  },
  {
    value: "makanan-tradisional",
    label: "Makanan",
    icon: <GiKnifeFork className="w-4 h-4" />,
  },
  {
    value: "tarian-tradisional",
    label: "Tarian",
    icon: <BsPersonArmsUp className="w-4 h-4" />,
  },
];

// --- Main Page ---
const SastraBudayaPage = () => {
  const [currentSection, setCurrentSection] = useState("Sastra & Budaya");
  // Dark mode state
  const [darkMode, setDarkMode] = useState(() => {
    const data = getUserData();
    return data?.meta?.darkMode ?? false;
  });

  // Listen for localStorage changes to keep darkMode in sync
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    const handleStorage = (e: StorageEvent) => {
      if (e.key === "galaxy_aksara_user_data") {
        const data = getUserData();
        setDarkMode(data?.meta?.darkMode ?? false);
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);
  const [activeTab, setActiveTab] = useState("cerita-rakyat");

  // Responsive breakpoints
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const isTablet = useMediaQuery({ minWidth: 768, maxWidth: 1023 });
  const isDesktop = useMediaQuery({ minWidth: 1024 });
  const isLandscape = useMediaQuery({ orientation: "landscape" });

  return (
    <div
      className={`${
        darkMode ? "bg-gray-900" : "bg-gray-100"
      } flex min-h-screen relative`}
    >
      <DotsBackground darkMode={darkMode} />
      {/* Left Sidebar: desktop shows full, tablet shows compact icon-only, mobile hides */}
      {isDesktop && (
        <LeftSidebar
          currentSection={currentSection}
          setCurrentSection={setCurrentSection}
          darkMode={darkMode}
        />
      )}
      {isTablet && !isMobile && !isDesktop && (
        <LeftSidebar
          currentSection={currentSection}
          setCurrentSection={setCurrentSection}
          darkMode={darkMode}
          compact
        />
      )}
      <div className="flex-1 flex">
        {/* Top Profile Bar (mobile/tablet only) */}
        {isMobile && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              zIndex: 30,
            }}
          >
            <TopProfileBar darkMode={darkMode}>
              {/* Only show profile section from RightSidebar */}
              <RightSidebar darkMode={darkMode} profileOnly />
            </TopProfileBar>
          </div>
        )}
        {/* Main content and sticky right sidebar in flex row */}
        <div className="flex flex-1 flex-row">
          <div
            className="flex-1 relative z-10 bg-transparent pt-0 md:pt-0"
            style={
              isMobile
                ? {
                    paddingTop: 64,
                    paddingBottom: 64,
                    paddingLeft: 16,
                    paddingRight: 16,
                  }
                : {}
            }
          >
            <main className="fpx-0 md:px-8">
              <div className="max-w-6xl mx-auto py-10 px-4">
                {/* Header */}
                <div className="text-center mb-8">
                  <h1
                    className={`text-3xl md:text-4xl font-extrabold mb-2 ${
                      darkMode ? "text-gray-100" : "text-gray-800"
                    }`}
                    style={{ fontFamily: "Feather Bold" }}
                  >
                    Sastra & Budaya Batak
                  </h1>
                  <p
                    className={`text-lg max-w-2xl mx-auto ${
                      darkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    Jelajahi kekayaan sastra dan budaya Batak melalui cerita
                    rakyat, lagu tradisional, adat istiadat, dan warisan budaya
                    lainnya yang telah turun temurun.
                  </p>
                </div>
                {/* Tabs */}
                <div className="grid w-full grid-cols-2 md:grid-cols-3 lg:grid-cols-6 mb-8 gap-2">
                  {tabs.map((tab) => (
                    <button
                      key={tab.value}
                      onClick={() => setActiveTab(tab.value)}
                      className={`flex items-center gap-2 justify-center px-3 py-2 rounded-lg font-semibold duration-200 transform hover:-translate-y-0.5 transition-all
                      ${
                        activeTab === tab.value
                          ? darkMode
                            ? "bg-orange-900 text-orange-200 shadow"
                            : "bg-orange-100 text-orange-800 shadow"
                          : darkMode
                          ? "bg-gray-800 text-gray-300 hover:bg-orange-950"
                          : "bg-white text-gray-600 hover:bg-orange-50"
                      }
                    `}
                    >
                      {tab.icon}
                      <span className="inline sm:inline md:hidden lg:inline">
                        {tab.label}
                      </span>
                    </button>
                  ))}
                </div>

                {/* Tab Contents */}
                <div>
                  {activeTab === "cerita-rakyat" && (
                    <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-3">
                      {stories.map((story, index) => (
                        <GenericCard
                          key={index}
                          image={story.image}
                          title={story.title}
                          subtitle={story.description}
                          region={story.region}
                          fields={[
                            { label: "Cerita", value: story.content },
                            { label: "Pesan Moral", value: story.moral },
                          ]}
                          references={story.references}
                          darkMode={darkMode}
                        />
                      ))}
                    </div>
                  )}

                  {activeTab === "lagu-tradisional" && (
                    <div className="grid gap-6">
                      {songs.map((song, index) => (
                        <SongCard key={index} song={song} darkMode={darkMode} />
                      ))}
                    </div>
                  )}

                  {activeTab === "adat-istiadat" && (
                    <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-2">
                      {customs.map((custom, index) => (
                        <GenericCard
                          key={index}
                          image={custom.image}
                          title={custom.title}
                          subtitle={custom.description}
                          fields={[
                            { label: "Proses", value: custom.process },
                            { label: "Makna", value: custom.significance },
                            { label: "Peserta", value: custom.participants },
                          ]}
                          references={custom.references}
                          darkMode={darkMode}
                        />
                      ))}
                    </div>
                  )}

                  {activeTab === "pakaian-adat" && (
                    <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-3">
                      {clothing.map((item, index) => (
                        <GenericCard
                          key={index}
                          image={item.image}
                          title={item.name}
                          subtitle={item.description}
                          fields={[
                            { label: "Penggunaan", value: item.usage },
                            { label: "Motif", value: item.pattern },
                            { label: "Makna", value: item.meaning },
                          ]}
                          references={item.references}
                          darkMode={darkMode}
                        />
                      ))}
                    </div>
                  )}

                  {activeTab === "makanan-tradisional" && (
                    <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-3">
                      {foods.map((food, index) => (
                        <GenericCard
                          key={index}
                          image={food.image}
                          title={food.name}
                          subtitle={food.description}
                          region={food.region}
                          fields={[
                            { label: "Bahan", value: food.ingredients },
                            { label: "Acara", value: food.occasion },
                            { label: "Makna", value: food.significance },
                          ]}
                          references={food.references}
                          darkMode={darkMode}
                        />
                      ))}
                    </div>
                  )}

                  {activeTab === "tarian-tradisional" && (
                    <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-2">
                      {dances.map((dance, index) => (
                        <GenericCard
                          key={index}
                          image={dance.image}
                          title={dance.name}
                          subtitle={dance.description}
                          fields={[
                            { label: "Gerakan", value: dance.movements },
                            { label: "Musik", value: dance.music },
                            { label: "Acara", value: dance.occasion },
                            { label: "Makna", value: dance.meaning },
                          ]}
                          references={dance.references}
                          darkMode={darkMode}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {(isMobile) && (
                <footer className="w-full pt-2 md:pt-2 pb-12 md:pb-10 bg-transparent flex flex-col items-center gap-2 z-10 relative">
                  <div
                    className="text-gray-500 text-sm font-semibold"
                    style={{ fontFamily: "DIN Next Rounded" }}
                  >
                    &copy; 2025 Galaxy Developer
                  </div>
                  <div className="flex gap-4 mt-1">
                    <a
                      href="https://www.instagram.com/galaxy.developer/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-gray-500 hover:text-pink-500 transition font-semibold text-sm"
                    >
                      <Instagram className="w-5 h-5" />
                    </a>
                    <a
                      href="https://www.linkedin.com/company/galaxydeveloper"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-gray-500 hover:text-blue-700 transition font-semibold text-sm"
                    >
                      <Linkedin className="w-5 h-5" />
                    </a>
                  </div>
                </footer>
              )}
            </main>
          </div>
          {(isDesktop || isTablet) && (
            <div
              style={{
                position: "sticky",
                top: 0,
                right: 0,
                height: "100vh",
                zIndex: 30,
                width: 320,
                background: "transparent",
                pointerEvents: "auto",
                overflowY: "auto",
                flexShrink: 0,
              }}
            >
              <RightSidebar darkMode={darkMode} />
            </div>
          )}
        </div>
        {/* Bottom Nav (mobile only) */}
        {isMobile && (
          <div
            style={{
              position: "fixed",
              bottom: 0,
              left: 0,
              width: "100vw",
              zIndex: 30,
              paddingLeft: 8,
              paddingRight: 8,
            }}
          >
            <BottomNav
              currentSection={currentSection}
              setCurrentSection={setCurrentSection}
              darkMode={darkMode}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SastraBudayaPage;
